import { memo } from 'react';
import { css } from '@linaria/core';
import { classnames } from '@app/common/utils/css';
import { CheckoutType } from '../utils/types';
import { useSdkDemoContext } from './Context';

export const Options = memo(() => {
  const { options, updateOptions } = useSdkDemoContext();

  return (
    <div className={classnames('options', styles.main)}>
      <div className="field_row">
        <div className="field">
          <label>Merchant Account</label>

          <input
            type="text"
            value={options.merchantAccount}
            onChange={(event) => updateOptions({ merchantAccount: event.target.value })}
          />
        </div>

        <div className="field">
          <label>Order ID</label>

          <input
            type="text"
            value={options.orderId}
            onChange={(event) => updateOptions({ orderId: event.target.value })}
          />
        </div>
      </div>

      <div className="field">
        <label>Order Summary</label>

        <input
          type="text"
          value={options.orderSummary}
          onChange={(event) => updateOptions({ orderSummary: event.target.value })}
        />
      </div>

      <div className="divider" />

      <div className="field">
        <label>Checkout Type</label>

        <div className="options">
          <TypeOption type={CheckoutType.hosted} />
          <TypeOption type={CheckoutType.modal} />
          <TypeOption type={CheckoutType.inline} />
        </div>
      </div>

      <div className="field_row">
        <div className="field">
          <label>Language</label>

          <select value={options.language} onChange={(event) => updateOptions({ language: event.target.value })}>
            <option value="">Detect Language</option>
            <optgroup label="Supported Languages">
              <option value="en">English</option>
              <option value="es">Spanish</option>
            </optgroup>
          </select>
        </div>
        <div className="field" />
      </div>

      <div className="field_row">
        <div className="field">
          <label>Header</label>

          <select
            value={options.showHeader.toString()}
            onChange={(event) => updateOptions({ showHeader: event.target.value === 'true' })}
          >
            <option value="true">Show</option>
            <option value="false">Hide</option>
          </select>
        </div>

        <div className="field">
          <label>Language Menu</label>

          <select
            value={options.showLanguageMenu.toString()}
            onChange={(event) => updateOptions({ showLanguageMenu: event.target.value === 'true' })}
          >
            <option value="true">Show</option>
            <option value="false">Hide</option>
          </select>
        </div>
      </div>

      <div className="divider" />

      <div className="inline_checkout">
        <label>Inline Checkout</label>

        <div id="inline_checkout_area"></div>
      </div>
    </div>
  );
});

const TypeOption = memo((props: { type: CheckoutType }) => {
  const { type } = props;

  const { options, updateOptions } = useSdkDemoContext();

  const label = type === CheckoutType.modal ? 'Modal' : type === CheckoutType.inline ? 'Inline' : 'Hosted';

  return (
    <RadioOption
      group="checkout_type"
      label={label}
      value={type}
      selected={options.type === type}
      onSelect={() => updateOptions({ type })}
    />
  );
});

const RadioOption = memo((props: { group: string; label: string; value: any; selected: boolean; onSelect(): void }) => {
  return (
    <div className="option" onClick={props.onSelect}>
      <input
        type="radio"
        id={`${props.group}_${props.value}`}
        name={props.group}
        value={props.value}
        checked={props.selected}
      />
      <label htmlFor="checkout_type_hosted">{props.label}</label>
    </div>
  );
});

const styles = {
  main: css`
    color: #222;

    label {
      display: inline-block;
    }

    .inline_checkout {
      margin-top: 1rem;
      width: 100%;

      label {
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 1rem;
      }

      #inline_checkout_area {
        width: 100%;
        height: 30rem;
        border: 1px solid #ccc;
        box-shadow: 0 0 10px #ccc;
        background-color: #fafafa;
      }
    }

    .divider {
      margin: 1.75rem 0 1.5rem;
      border-bottom: 1px dotted #ddd;
    }

    .field_row {
      margin-top: 1rem;
      display: flex;
      flex-direction: row;
      align-items: flex-start;

      .field {
        flex: 1;
        margin: 0 1rem;

        &:first-of-type {
          margin-left: 0;
        }

        &:last-of-type {
          margin-right: 0;
        }
      }
    }

    .field {
      display: flex;
      flex-direction: column;
      margin-top: 1rem;

      > label {
        font-size: 0.9rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }

      > select,
      > input {
        appearance: none;
        padding: 0.5rem 0.75rem;
        border-radius: 5px;
        border: 1px solid #ccc;
        box-shadow: 0 0 10px #eee;
        font-size: 1rem;
        font-family: inherit;
      }
    }

    .options {
      display: flex;
      flex-direction: row;
      align-items: center;

      .option {
        margin-right: 2rem;
      }
    }

    .option {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: 0.5rem;

      input {
        margin-right: 0.5rem;
        appearance: none;

        width: 1rem;
        height: 1rem;
        background: #fff;
        border: 2px solid #555;
        border-radius: 50%;

        &:checked {
          border: 6px solid var(--color-primary);
        }
      }

      label {
        font-size: 1rem;
      }
    }
  `,
};
